import {
  applicationDataForDocumentHash,
  getAttestationDisclaimerText,
  getDocumentHashPayload,
} from '@access-cms/shared-constants';

import type { SignatureData } from '../../../components/DigitalSignatureModal';
import { toPersistedApplicationData } from '../context/application-map';
import type { WorkflowFormData } from '../config';

export type AttestationSignatureType = 'styled' | 'drawn';

export interface AttestationPayload {
  agreed: boolean;
  attestationTextHash: string;
  canvasFingerprint?: string;
  colorDepth?: number;
  deviceType: 'desktop' | 'mobile' | 'tablet';
  documentHash: string;
  fullName: string;
  screenResolution?: string;
  signatureData?: string;
  signatureDocumentId?: string;
  signatureMimeType?: 'image/png';
  signatureType: AttestationSignatureType;
  signedAt: string;
  timezone: string;
  userAgent: string;
}

function fallbackSha256Hex(value: string): string {
  let hash = 0;

  for (let index = 0; index < value.length; index += 1) {
    const char = value.charCodeAt(index);
    hash = (hash << 5) - hash + char;
    hash &= hash;
  }

  const positiveHash = Math.abs(hash).toString(16).padStart(16, '0');

  return (positiveHash + positiveHash).padEnd(64, '0').slice(0, 64);
}

export async function sha256Hex(value: string): Promise<string> {
  const encoder = new TextEncoder();
  const data = encoder.encode(value);

  try {
    if (!globalThis.crypto?.subtle) {
      return fallbackSha256Hex(value);
    }

    const hashBuffer = await globalThis.crypto.subtle.digest('SHA-256', data);

    return Array.from(new Uint8Array(hashBuffer))
      .map((byte) => byte.toString(16).padStart(2, '0'))
      .join('');
  } catch {
    return fallbackSha256Hex(value);
  }
}

export async function computeDocumentHash(applicationData: Record<string, unknown>): Promise<string> {
  return sha256Hex(getDocumentHashPayload(applicationData));
}

function detectDeviceType(): AttestationPayload['deviceType'] {
  const userAgent = navigator.userAgent;

  if (/tablet|ipad|silk/i.test(userAgent)) {
    return 'tablet';
  }

  if (/mobile|iphone|ipod|android.*mobile/i.test(userAgent)) {
    return 'mobile';
  }

  return 'desktop';
}

export interface BuildAttestationOptions {
  applicationTitle: string;
  formData: WorkflowFormData;
  signatureData: SignatureData;
  signatureDocumentId?: string;
  /** When set, document hash is derived from persisted server data (post-sync), not form state. */
  persistedApplicationData?: Record<string, unknown>;
}

export async function buildAttestationFromSignature(options: BuildAttestationOptions): Promise<AttestationPayload> {
  const { applicationTitle, formData, signatureData, signatureDocumentId, persistedApplicationData } = options;
  const applicationData = persistedApplicationData ?? toPersistedApplicationData(formData);
  const disclaimerText = getAttestationDisclaimerText(applicationTitle);

  const [documentHash, attestationTextHash] = await Promise.all([
    computeDocumentHash(applicationData),
    sha256Hex(disclaimerText),
  ]);

  const basePayload = {
    agreed: true,
    attestationTextHash,
    canvasFingerprint: signatureData.browserFingerprint,
    colorDepth: screen.colorDepth,
    deviceType: detectDeviceType(),
    documentHash,
    fullName: signatureData.fullName,
    screenResolution: `${screen.width}x${screen.height}`,
    signedAt: signatureData.timestamp,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    userAgent: navigator.userAgent,
  };

  if (signatureData.type === 'draw') {
    if (!signatureDocumentId) {
      throw new Error('Drawn signature must be uploaded before attestation');
    }

    return {
      ...basePayload,
      signatureDocumentId,
      signatureMimeType: 'image/png',
      signatureType: 'drawn',
    };
  }

  if (!signatureData.fontFamily) {
    throw new Error('Styled signature font is required');
  }

  return {
    ...basePayload,
    signatureData: signatureData.fontFamily,
    signatureType: 'styled',
  };
}

export { applicationDataForDocumentHash, getAttestationDisclaimerText };
